import React from 'react';
import * as Yup from 'yup';
import { Grid, Typography } from '@mui/material';
import {
  SparkDropdown,
  SparkForm,
  SparkFormTaggedAutoComplete,
} from 'sharedComponents/SparkForm';
import { useLegalStructures } from 'hooks/LegalStructures/useLegalStructures';
import { useGeographicMarkets } from 'hooks/GeographicMarkets/useGeographicMarkets';
import { useMarketApproaches } from 'hooks/MarketApproaches/useMarketApproaches';
import { useBusinessStages } from 'hooks/BusinessStages/useBusinessStages';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import Footer from './Footer';

export type CompanyOverViewFormValues = {
  companyStageID: string;
  legalStructureID: string;
  marketApproaches: string[] | '';
  geographicMarketID: string;
};

type CompanyOverViewFormProps = {
  activeStep: number;
  nextStepHandler: () => void;
  previousStepHandler: () => void;
  overViewCollection?: CompanyOverViewFormValues;
  setOverViewCollection: (formValues: CompanyOverViewFormValues) => void;
};

function CompanyOverViewForm({
  nextStepHandler,
  activeStep,
  previousStepHandler,
  overViewCollection,
  setOverViewCollection,
}: CompanyOverViewFormProps) {
  const {
    dropdownOptions: legalStructures,
    isLoading: isLoadingLegalStructures,
  } = useLegalStructures();
  const {
    dropdownOptions: geographicMarkets,
    isLoading: isLoadingGeographicMarkets,
  } = useGeographicMarkets();
  const {
    dropdownOptions: marketApproaches,
    isLoading: isLoadingMarketApproaches,
  } = useMarketApproaches();
  const {
    dropdownOptions: businessStages,
    isLoading: isLoadingBusinessStages,
  } = useBusinessStages();

  const companyOverViewFormValidationSchema = Yup.object({
    companyStageID: Yup.string().required('Required'),
    legalStructureID: Yup.string().required('Required'),
    marketApproaches: Yup.array()
      .min(1, 'Select at least one market approach')
      .required('Required'),
    geographicMarketID: Yup.string().required('Required'),
  });

  const companyOverViewFormInitialValues = {
    companyStageID: overViewCollection?.companyStageID || '',
    legalStructureID: overViewCollection?.legalStructureID || '',
    marketApproaches: overViewCollection?.marketApproaches ?? [],
    geographicMarketID: overViewCollection?.geographicMarketID || '',
  };
  const handleSubmit = async (values: CompanyOverViewFormValues) => {
    setOverViewCollection(values);
    nextStepHandler();
  };
  const isLoading =
    isLoadingBusinessStages ||
    isLoadingGeographicMarkets ||
    isLoadingLegalStructures ||
    isLoadingMarketApproaches;
  if (isLoading) {
    return <FieldSkeleton count={4} />;
  }
  return (
    <SparkForm
      initialValues={companyOverViewFormInitialValues}
      onSave={handleSubmit}
      validationSchema={companyOverViewFormValidationSchema}
    >
      <Grid item>
        <Typography
          fontFamily='Mont-semibold'
          justifyContent='flex-start'
          sx={{ fontSize: '1.5rem', marginTop: '1rem' }}
        >
          Company Overview
        </Typography>
        <Typography
          fontFamily='Mont-regular'
          justifyContent='flex-start'
          sx={{ fontSize: '1.0rem' }}
          color='nightblack'
          paddingBottom={2}
        >
          Tell us more about your company.
        </Typography>
      </Grid>

      <Grid container>
        <SparkDropdown
          size={12}
          name='companyStageID'
          label='Company Stage'
          children={[...businessStages]}
        />

        <SparkDropdown
          size={12}
          name='legalStructureID'
          label='Legal Structure'
          children={[...legalStructures]}
        />

        <SparkFormTaggedAutoComplete
          size={12}
          name='marketApproaches'
          label='Market Approach'
          placeholder='Please select up to 3'
          maxSelections={3}
          options={[...marketApproaches]}
        />

        <SparkDropdown
          size={12}
          name='geographicMarketID'
          label='Geographic Market'
          children={[...geographicMarkets]}
          helpText='Select the primary area where your business operates to target
        customers.'
        />
      </Grid>
      <Footer
        activeStep={activeStep}
        handleBack={previousStepHandler}
        isLastStep={false}
      />
    </SparkForm>
  );
}

export default CompanyOverViewForm;
